import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { api } from "./api";

export type User = {
  id: string;
  email: string | null;
  name: string | null;
  image: string | null;
  role: "user" | "admin";
};

export type Session = {
  user: User;
} | null;

type AuthContextValue = {
  session: Session;
  isLoading: boolean;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
  isTestAuthEnabled: boolean;
  csrfToken: string | null;
  /** Trigger Google OAuth sign-in directly (skipping sign-in page) */
  signInWithGoogle: (callbackUrl?: string) => void;
};

const AuthContext = createContext<AuthContextValue>({
  session: null,
  isLoading: true,
  signOut: async () => {},
  refreshSession: async () => {},
  isTestAuthEnabled: false,
  csrfToken: null,
  signInWithGoogle: () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isTestAuthEnabled, setIsTestAuthEnabled] = useState(false);
  const [csrfToken, setCsrfToken] = useState<string | null>(null);

  useEffect(() => {
    async function fetchTestConfig() {
      const res = await api.test.config.$get();
      if (res.ok) {
        const data = await res.json();
        setIsTestAuthEnabled(data.enableTestAuth);
      }
    }
    fetchTestConfig().catch(() => {});
  }, []);

  useEffect(() => {
    async function fetchCsrf() {
      const res = await fetch("/api/auth/csrf");
      const data: { csrfToken: string } = await res.json();
      setCsrfToken(data.csrfToken);
    }
    fetchCsrf().catch(() => {});
  }, []);

  const fetchSession = async () => {
    try {
      const res = await api.auth.me.$get();
      if (res.ok) {
        const data = await res.json();
        if ("user" in data) {
          setSession(data as Session);
        } else {
          setSession(null);
        }
      } else {
        setSession(null);
      }
    } catch {
      setSession(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSession();
  }, []);

  const signOut = async () => {
    try {
      const csrfRes = await fetch("/api/auth/csrf", { credentials: "include" });
      const { csrfToken } = (await csrfRes.json()) as { csrfToken: string };

      await fetch("/api/auth/signout", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: `csrfToken=${encodeURIComponent(csrfToken)}`,
      });
    } finally {
      setSession(null);
      window.location.href = "/";
    }
  };

  const refreshSession = async () => {
    setIsLoading(true);
    await fetchSession();
  };

  const signInWithGoogle = (callbackUrl?: string) => {
    if (!csrfToken) {
      console.error("CSRF token not available for OAuth");
      return;
    }

    if (callbackUrl) {
      sessionStorage.setItem("redirectAfterSignIn", callbackUrl);
    }

    const form = document.createElement("form");
    form.method = "POST";
    form.action = "/api/auth/signin/google";

    function addHiddenInput(name: string, value: string) {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value;
      form.appendChild(input);
    }

    addHiddenInput("csrfToken", csrfToken);
    if (callbackUrl) {
      addHiddenInput("callbackUrl", callbackUrl);
    }

    document.body.appendChild(form);
    form.submit();
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        isLoading,
        signOut,
        refreshSession,
        isTestAuthEnabled,
        csrfToken,
        signInWithGoogle,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useSession() {
  return useContext(AuthContext);
}

export { AuthContext };

export function useIsAdmin() {
  const { session } = useSession();
  return session?.user?.role === "admin";
}
